import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { moduleService } from "@/services/module.service";
import { tenantService } from "@/services/tenant.service";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { useModuleCoverage } from "./hooks/useModuleCoverage";
import { useModuleEditor } from "./hooks/useModuleEditor";
import {
  RouteCoverageTable,
  PermissionCoverageTable,
  ModuleListPanel,
  ModuleDetailPanel,
} from "./components";

type Tab = "modules" | "routes" | "permissions" | "bulk";

const TABS: { id: Tab; label: string }[] = [
  { id: "modules", label: "Modules" },
  { id: "routes", label: "Route Coverage" },
  { id: "permissions", label: "Permissions" },
  { id: "bulk", label: "Bulk Assignment" },
];

function ModuleCoveragePage() {
  const [activeTab, setActiveTab] = useState<Tab>("modules");
  const [tenantSearch, setTenantSearch] = useState("");
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [selectedModuleCodes, setSelectedModuleCodes] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<"enable" | "disable">("enable");
  const [bulkReason, setBulkReason] = useState("");

  const coverage = useModuleCoverage();
  const editor = useModuleEditor();
  const queryClient = useQueryClient();

  const { data: tenants } = useQuery({
    queryKey: ["module-bulk-tenants", tenantSearch],
    queryFn: () =>
      tenantService.list({
        page: 1,
        limit: 50,
        search: tenantSearch.trim() || undefined,
      }),
  });

  const { mutateAsync: bulkAssignModules, isPending: isBulkAssigning } =
    useMutation({
      mutationFn: () =>
        moduleService.bulkAssignModules({
          tenantIds: selectedTenantIds,
          moduleCodes: selectedModuleCodes,
          action: bulkAction,
          reason: bulkReason.trim(),
        }),
      onSuccess: (result) => {
        toast.success(
          `${result.successful} assignment(s) completed${result.failed ? `, ${result.failed} failed` : ""}`,
        );
        setSelectedTenantIds([]);
        setSelectedModuleCodes([]);
        setBulkReason("");
        queryClient.invalidateQueries({ queryKey: ["admin-modules"] });
      },
      onError: (error: any) =>
        toast.error(error?.message || "Bulk assignment failed"),
    });

  const handleSelectModule = (mod: any) => {
    editor.startEditingModule(mod);
  };

  const handleCancel = () => {
    editor.setEditingModule(null);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Module Management"
        subtitle="Manage entitlements and permissions; inspect the code-owned page-policy registry."
      />

      {/* Tab Bar */}
      <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "modules" && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div
            className="grid grid-cols-[280px_1fr] [&>*]:min-h-0"
            style={{ height: "calc(100vh - 220px)" }}
          >
            {/* Left: Module List */}
            <ModuleListPanel
              modules={coverage.modules}
              selectedModule={editor.editingModule}
              onSelectModule={handleSelectModule}
              getModuleIcon={editor.getModuleIcon}
            />

            {/* Right: Detail Panel */}
            {editor.editingModule ? (
              <ModuleDetailPanel
                editingModule={editor.editingModule}
                editModuleDraft={editor.editModuleDraft}
                setEditModuleDraft={editor.setEditModuleDraft}
                onSave={editor.updateModule}
                onCancel={handleCancel}
                isSaving={editor.isUpdatingModule}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-400">
                Select a module to edit its database-owned metadata
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "routes" && (
        <RouteCoverageTable moduleMap={coverage.moduleMap} />
      )}

      {activeTab === "permissions" && (
        <PermissionCoverageTable
          modules={coverage.modules}
          permissions={coverage.permissions}
          permissionsLoading={coverage.permissionsLoading}
          updatePermission={coverage.updatePermission}
          isUpdating={coverage.isUpdating}
        />
      )}

      {activeTab === "bulk" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Select Tenants
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Search tenants and choose who should receive the module
                  override.
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {selectedTenantIds.length} selected
              </span>
            </div>

            <input
              value={tenantSearch}
              onChange={(event) => setTenantSearch(event.target.value)}
              placeholder="Search tenants"
              className="mt-4 h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
            />

            <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
              {tenants?.data.map((tenant) => {
                const checked = selectedTenantIds.includes(tenant.id);
                return (
                  <label
                    key={tenant.id}
                    className="flex items-start gap-3 rounded-xl border border-slate-100 px-4 py-3 text-sm hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelectedTenantIds((current) =>
                          checked
                            ? current.filter((value) => value !== tenant.id)
                            : [...current, tenant.id],
                        )
                      }
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <span>
                      <span className="block font-medium text-slate-900">
                        {tenant.name}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {tenant.email} · {tenant.status}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
              Configure Assignment
            </h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Action
                </label>
                <select
                  value={bulkAction}
                  onChange={(event) =>
                    setBulkAction(event.target.value as "enable" | "disable")
                  }
                  className="mt-2 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="enable">Enable modules</option>
                  <option value="disable">Disable modules</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Reason
                </label>
                <input
                  value={bulkReason}
                  onChange={(event) => setBulkReason(event.target.value)}
                  placeholder="Required override reason (8+ characters)"
                  className="mt-2 h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Modules
                </label>
                <div className="mt-2 max-h-[280px] space-y-2 overflow-y-auto pr-1">
                  {coverage.modules.map((mod) => {
                    const checked = selectedModuleCodes.includes(mod.code);
                    return (
                      <label
                        key={mod.code}
                        className="flex items-start gap-3 rounded-xl border border-slate-100 px-4 py-3 text-sm hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedModuleCodes((current) =>
                              checked
                                ? current.filter((value) => value !== mod.code)
                                : [...current, mod.code],
                            )
                          }
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                        />
                        <span>
                          <span className="block font-medium text-slate-900">
                            {mod.name}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {mod.code}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                disabled={
                  !selectedTenantIds.length ||
                  !selectedModuleCodes.length ||
                  bulkReason.trim().length < 8 ||
                  isBulkAssigning
                }
                onClick={() => bulkAssignModules()}
                className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-50"
              >
                {isBulkAssigning ? "Applying..." : "Apply bulk assignment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModuleCoveragePage;
